import React from "react";

import { css } from "@compiled/react";
import {
    CommonNode,
    documentToHtmlString,
    Next
} from "@contentful/rich-text-html-renderer";
import {
    Document,
    Node as ContentfulNode,
    BLOCKS,
    INLINES,
    TopLevelBlock
} from "@contentful/rich-text-types";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Image,
    Panel,
    PullQuote,
    EmbeddedVideo,
    Button,
    ContentBlock
} from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { RichTextComponent } from "@/libraries/design/utils";

export interface UniformBodyContentProps {
    body: Document;
}

interface Node extends ContentfulNode {
    nodeType: BLOCKS | INLINES | "text";
    content?: Node[];
    value?: string;
    marks?: { type: string }[];
    data: { target?: { fields?: object } };
}

interface EmbeddedEntryNode extends Node {
    data: {
        target: {
            fields: object;
            sys: { contentType: { sys: { id: string } } };
        };
    };
}

type embeddedAssetBlockDataType = {
    fields: {
        file: {
            contentType: string;
            details: { image: { width: number; height: number }; size: number };
            fileName: string;
            url: string;
        };
        description: string;
        title: string;
    };
};

type embeddedVideoAssetDataType = {
    embedded?: string;
    embedAssetOverlay?: embeddedAssetBlockDataType;
    embedAssetOverlayOl?: { fields: orangeLogicAssetDataType };
    embeddedVideoAssetNameExternal?: string;
};

type proTipComponentDataType = {
    proTipNameExternal: string;
    proTipRichText?: Document;
    proTipText?: string;
};

type callOutComponentDataType = {
    callOutComponentTitleExternal: string;
    callOutText?: Document;
};

type callOutDataType = {
    callOutTitleExternal: string;
    callOutShortDescription?: Document;
    callToActionText?: string;
    urlLink?: string;
    orangeLogicImage?: { fields: orangeLogicAssetDataType };
};

type beforeYouBeginDataType = {
    time?: string;
    audience?: Document;
    prerequisite?: Document;
};

type orangeLogicAssetDataType = {
    assetJson?: {
        imageUrl: string;
    };
    assetUrl?: string;
    alt?: string;
    width?: string;
};

type callToActionMicrocopyDataType = {
    ctaButtonText: string;
    ctaUrl: string;
};

type codeBlockDataType = {
    code: string;
};

type anchorLinkDataType = {
    id: string;
};

const UniformBodyContent = ({ body }: UniformBodyContentProps) => {
    const intl = useIntl();

    const customComponentRenderer = (
        node: EmbeddedEntryNode
        // eslint-disable-next-line complexity
    ): React.ReactElement => {
        switch (node.data.target.sys.contentType.sys.id) {
            case "embeddedVideoAsset": {
                const embeddedVideoAssetData = node.data.target
                    .fields as embeddedVideoAssetDataType;
                let embeddedVideoThumbnail = undefined;
                if (embeddedVideoAssetData.embedAssetOverlayOl) {
                    const orangeLogicThumbnail =
                        embeddedVideoAssetData.embedAssetOverlayOl.fields;
                    const imageUrl = orangeLogicThumbnail.assetJson
                        ? orangeLogicThumbnail.assetJson.imageUrl
                        : orangeLogicThumbnail.assetUrl ?? "";

                    embeddedVideoThumbnail = {
                        src: imageUrl,
                        alt: orangeLogicThumbnail.alt ?? ""
                    };
                } else if (embeddedVideoAssetData.embedAssetOverlay) {
                    embeddedVideoThumbnail = {
                        src: embeddedVideoAssetData.embedAssetOverlay.fields
                            .file.url,
                        alt: embeddedVideoAssetData.embedAssetOverlay.fields
                            .description,
                        contentType:
                            embeddedVideoAssetData.embedAssetOverlay.fields.file
                                .contentType
                    };
                }
                if (!embeddedVideoAssetData.embedded) {
                    return <></>;
                }
                return (
                    <div css={css(embeddedItemStyles)}>
                        <EmbeddedVideo
                            embedUrl={embeddedVideoAssetData.embedded}
                            thumbnail={embeddedVideoThumbnail}
                            title={
                                embeddedVideoAssetData.embeddedVideoAssetNameExternal ??
                                ""
                            }
                        />
                    </div>
                );
            }
            case "proTipComponent": {
                const proTipComponentData = node.data.target
                    .fields as proTipComponentDataType;
                return (
                    <div css={css(embeddedItemStyles)}>
                        <Panel
                            panelType="pro-tip"
                            label={proTipComponentData.proTipNameExternal}
                            description={
                                proTipComponentData.proTipRichText ? (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: documentToHtmlString(
                                                proTipComponentData.proTipRichText
                                            )
                                        }}
                                    />
                                ) : (
                                    proTipComponentData.proTipText ?? ""
                                )
                            }
                        />
                    </div>
                );
            }
            case "callOutComponent": {
                const callOutComponentData = node.data.target
                    .fields as callOutComponentDataType;
                return (
                    <div css={css(embeddedItemStyles)}>
                        <Panel
                            panelType="highlight"
                            label={
                                callOutComponentData.callOutComponentTitleExternal
                            }
                            description={
                                callOutComponentData.callOutText ? (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: documentToHtmlString(
                                                callOutComponentData.callOutText
                                            )
                                        }}
                                    />
                                ) : (
                                    ""
                                )
                            }
                        />
                    </div>
                );
            }
            case "callOut": {
                const callOutData = node.data.target.fields as callOutDataType;
                const imageUrl = callOutData.orangeLogicImage?.fields.assetJson
                    ? callOutData.orangeLogicImage?.fields.assetJson.imageUrl
                    : callOutData.orangeLogicImage?.fields.assetUrl ??
                      undefined;
                const sideBySideContentCss = css({
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: token("space.200")
                });
                return (
                    <div css={[css(embeddedItemStyles), sideBySideContentCss]}>
                        {imageUrl && (
                            <Image
                                src={imageUrl}
                                alt={
                                    callOutData.orangeLogicImage?.fields.alt ??
                                    ""
                                }
                                style={
                                    callOutData.orangeLogicImage?.fields.width
                                        ? {
                                              width: callOutData
                                                  .orangeLogicImage?.fields
                                                  .width
                                          }
                                        : undefined
                                }
                            />
                        )}
                        <ContentBlock
                            title={callOutData.callOutTitleExternal}
                            text={
                                callOutData.callOutShortDescription ? (
                                    <RichTextComponent
                                        text={documentToHtmlString(
                                            callOutData.callOutShortDescription
                                        )}
                                    />
                                ) : (
                                    ""
                                )
                            }
                            callToActionProps={
                                callOutData.callToActionText &&
                                callOutData.urlLink
                                    ? {
                                          link: {
                                              text: callOutData.callToActionText,
                                              href: callOutData.urlLink
                                          }
                                      }
                                    : undefined
                            }
                        />
                    </div>
                );
            }
            case "beforeYouBeginComponent": {
                const beforeYouBeginData = node.data.target
                    .fields as beforeYouBeginDataType;
                const beforeYouBeginContainerCss = css({
                    backgroundColor: wacToken("color.neutral.light200"),
                    padding: token("space.400"),
                    gap: token("space.200"),
                    display: "flex",
                    flexDirection: "column"
                });
                const individualItemContainerCss = css({
                    display: "flex",
                    flexDirection: "column",
                    [media.above.sm]: { gap: token("space.100") },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    ["h3, p"]: { margin: 0 }
                });
                return (
                    <div
                        css={[
                            css(embeddedItemStyles),
                            beforeYouBeginContainerCss
                        ]}
                    >
                        {beforeYouBeginData.time && (
                            <div css={individualItemContainerCss}>
                                <h3 className="eyebrow-lg">
                                    {intl.formatMessage({
                                        id: "beforeYouBegin.time",
                                        defaultMessage: "Time"
                                    })}
                                </h3>
                                <p>{beforeYouBeginData.time}</p>
                            </div>
                        )}
                        {beforeYouBeginData.audience && (
                            <div css={individualItemContainerCss}>
                                <h3 className="eyebrow-lg">
                                    {intl.formatMessage({
                                        id: "beforeYouBegin.audience",
                                        defaultMessage: "Audience"
                                    })}
                                </h3>
                                <RichTextComponent
                                    text={documentToHtmlString(
                                        beforeYouBeginData.audience
                                    )}
                                />
                            </div>
                        )}
                        {beforeYouBeginData.prerequisite && (
                            <div css={individualItemContainerCss}>
                                <h3 className="eyebrow-lg">
                                    {intl.formatMessage({
                                        id: "beforeYouBegin.prerequisite",
                                        defaultMessage: "Prerequisite"
                                    })}
                                </h3>
                                <RichTextComponent
                                    text={documentToHtmlString(
                                        beforeYouBeginData.prerequisite
                                    )}
                                />
                            </div>
                        )}
                    </div>
                );
            }
            case "orangeLogicAsset": {
                const orangeLogicAssetData = node.data.target
                    .fields as orangeLogicAssetDataType;
                const imageUrl = orangeLogicAssetData.assetJson
                    ? orangeLogicAssetData.assetJson.imageUrl
                    : orangeLogicAssetData.assetUrl ?? undefined;
                if (imageUrl === undefined) {
                    return <></>;
                }
                return (
                    <div
                        css={[
                            css({
                                width: "100%",
                                display: "flex",
                                justifyContent: "center"
                            }),
                            css(embeddedItemStyles)
                        ]}
                    >
                        <Image
                            src={imageUrl}
                            alt={orangeLogicAssetData.alt ?? ""}
                            style={
                                orangeLogicAssetData.width
                                    ? {
                                          width: orangeLogicAssetData.width
                                      }
                                    : undefined
                            }
                        />
                    </div>
                );
            }
            case "callToActionMicrocopy": {
                const callToActionMicrocopyData = node.data.target
                    .fields as callToActionMicrocopyDataType;
                return (
                    <div css={css(embeddedItemStyles)}>
                        <Button
                            href={callToActionMicrocopyData.ctaUrl}
                            dataEventProps={{
                                "data-product-key": "uniform",
                                "data-event-component": "linkButton",
                                "data-event-container": "bodyContentInline",
                                "data-event": "clicked"
                            }}
                        >
                            {callToActionMicrocopyData.ctaButtonText}
                        </Button>
                    </div>
                );
            }
            case "codeBlock": {
                const codeBlockData = node.data.target
                    .fields as codeBlockDataType;
                return (
                    <RichTextComponent
                        text={`<pre><code>${codeBlockData.code}</code></pre>`}
                    />
                );
            }
            default: {
                return <></>;
            }
        }
    };

    const renderContentfulRichTextNodes = (nodes: Node[]) => {
        return nodes.map((node) => {
            switch (node.nodeType) {
                case "embedded-entry-block": {
                    return customComponentRenderer(node as EmbeddedEntryNode);
                }
                case "embedded-asset-block": {
                    const embeddedAssetBlockData = node.data
                        .target as embeddedAssetBlockDataType;
                    return (
                        <Image
                            src={embeddedAssetBlockData.fields.file.url}
                            alt={embeddedAssetBlockData.fields.description}
                            style={embeddedItemStyles}
                        />
                    );
                }
                case "blockquote": {
                    return (
                        <PullQuote
                            quote=""
                            quoteRichTextString={parseRichTextStringForOnlyContentInsideParagraphs(
                                documentToHtmlString({
                                    nodeType: BLOCKS.DOCUMENT,
                                    content: node.content as TopLevelBlock[],
                                    data: {}
                                })
                            )}
                        />
                    );
                }
                default: {
                    const headingRenderer = (node: Node, next: Next) => {
                        const headingNumber = node.nodeType.split("-")[1];
                        const marks = node.content?.at(0)?.marks ?? [];
                        marks?.filter((mark) => mark.type.includes("id:"));
                        const id = marks[0]?.type.replace("id:", "");

                        return `<h${headingNumber}${
                            id ? ` id="${id}"` : ""
                        }>${next(
                            node.content as CommonNode[]
                        )}</h${headingNumber}>`;
                    };
                    const options = {
                        renderNode: {
                            [BLOCKS.HEADING_2]: headingRenderer,
                            [BLOCKS.HEADING_3]: headingRenderer,
                            [BLOCKS.HEADING_4]: headingRenderer,
                            [BLOCKS.HEADING_5]: headingRenderer,
                            [BLOCKS.HEADING_6]: headingRenderer
                        }
                    };
                    return (
                        <RichTextComponent
                            text={documentToHtmlString(
                                {
                                    nodeType: BLOCKS.DOCUMENT,
                                    content: [
                                        parseRichTextNodesForInlineEntries(
                                            node
                                        ) as TopLevelBlock
                                    ],
                                    data: {}
                                },
                                options
                            )}
                        />
                    );
                }
            }
        });
    };

    return (
        <div id="body-content" css={bodyContainerCss}>
            {renderContentfulRichTextNodes(body.content)}
        </div>
    );
};

const embeddedItemStyles = {
    maxWidth: "100%",
    marginBlock: token("space.200")
};

const bodyContainerCss = css({
    display: "flex",
    flexDirection: "column",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div:has(p:last-child) + div:has(p:first-child)"]: {
        // will only apply to divs with paragraphs that immediately follow another div with paragraph
        marginBlockStart: token("space.200")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div:first-child:has(h2) h2:first-child"]: {
        margin: 0
    }
});

const parseRichTextStringForOnlyContentInsideParagraphs = (
    richTextString: string
): string => {
    const paragraphRegex = /<p\b[^>]*>(.*?)<\/p>/gi;
    const matches = Array.from(richTextString.matchAll(paragraphRegex));
    return matches ? matches.map((m) => m[1]).join() : richTextString;
};

const parseRichTextNodesForInlineEntries = (node: Node): Node => {
    if (
        node.nodeType.split("-")[0] === "heading" &&
        node.content &&
        node.content.length > 1
    ) {
        // check if anchor exists in heading node
        const anchorIndex = node.content.findIndex(
            (innerNode) =>
                innerNode.nodeType === INLINES.EMBEDDED_ENTRY &&
                (innerNode as EmbeddedEntryNode).data.target?.sys?.contentType
                    .sys.id === "anchorLink"
        );
        if (anchorIndex > 0) {
            const anchorLinkData = node.content.at(anchorIndex)?.data.target
                ?.fields as anchorLinkDataType;
            const headerTextNode = node.content.at(0);
            if (headerTextNode && headerTextNode.marks) {
                // set mark id:<id> on header text node
                headerTextNode.marks.push({ type: `id:${anchorLinkData.id}` });
                node.content.splice(anchorIndex, 1);
            }
        }
    }
    return node;
};

export default UniformBodyContent;
